import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { DEFAULT_CURRENCY } from '@/constants/currency';

const SELECTED_CURRENCY_STORAGE_KEY = 'selected_currency';
const SELECTED_CURRENCY_TYPE_STORAGE_KEY = 'selected_currency_type';
const DEFAULT_CURRENCY_TYPE = 'fiat';

type SelectedCurrencyContextValue = {
  selectedCurrency: string;
  selectedCurrencyType: string;
  setSelectedCurrency: (currency: string, type?: string) => void;
};

const SelectedCurrencyContext = createContext<SelectedCurrencyContextValue | undefined>(undefined);

const getInitialCurrencyState = (): { currency: string; type: string } => {
  if (typeof window === 'undefined') {
    return { currency: DEFAULT_CURRENCY, type: DEFAULT_CURRENCY_TYPE };
  }

  const storedCurrency = window.localStorage.getItem(SELECTED_CURRENCY_STORAGE_KEY)?.trim();
  const storedType = window.localStorage.getItem(SELECTED_CURRENCY_TYPE_STORAGE_KEY)?.trim();

  return {
    currency: storedCurrency || DEFAULT_CURRENCY,
    type: storedType || DEFAULT_CURRENCY_TYPE,
  };
};

export const SelectedCurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [{ currency: selectedCurrency, type: selectedCurrencyType }, setSelectedCurrencyState] =
    useState(getInitialCurrencyState);

  const setSelectedCurrency = useCallback((currency: string, type?: string) => {
    const normalizedCurrency = currency.trim() || DEFAULT_CURRENCY;
    const normalizedType = type?.trim() || DEFAULT_CURRENCY_TYPE;

    setSelectedCurrencyState({
      currency: normalizedCurrency,
      type: normalizedType,
    });

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SELECTED_CURRENCY_STORAGE_KEY, normalizedCurrency);
      window.localStorage.setItem(SELECTED_CURRENCY_TYPE_STORAGE_KEY, normalizedType);
    }
  }, []);

  const value = useMemo(
    () => ({
      selectedCurrency,
      selectedCurrencyType,
      setSelectedCurrency,
    }),
    [selectedCurrency, selectedCurrencyType, setSelectedCurrency],
  );

  return (
    <SelectedCurrencyContext.Provider value={value}>{children}</SelectedCurrencyContext.Provider>
  );
};

export const useSelectedCurrency = (): SelectedCurrencyContextValue => {
  const context = useContext(SelectedCurrencyContext);

  if (!context) {
    throw new Error('useSelectedCurrency must be used within SelectedCurrencyProvider');
  }

  return context;
};
